import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/Badge'
import { Mail, RefreshCw, Settings, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { toast } from '@/hooks/use-toast'
import {
  useTriggerFetchEmails,
  useWorkflowEmailSettings,
  useWorkflowErpSettings,
  useWorkflowFlow,
} from '@/features/admin/workflowSetupQueries'
import { ConnectionState, formatTimestamp } from '@/features/admin/components/ConnectionState'
import { useIsRootOrganization } from '@/features/admin/useIsRootOrganization'
import { ERP_PROVIDERS, WORKFLOW_TYPES } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

/** A card in the row. Left to right is the order a run moves: mail in, flow, ERP out. */
function Node({
  icon,
  title,
  subtitle,
  children,
  action,
  emphasis = false,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  children?: React.ReactNode
  action?: React.ReactNode
  emphasis?: boolean
}) {
  return (
    <div
      className={
        emphasis
          ? 'flex flex-col rounded-panel border-2 border-primary/30 bg-gradient-to-b from-brand-100 to-surface p-5 shadow-panel'
          : 'flex flex-col rounded-panel border border-canvas-border bg-surface p-5 shadow-panel'
      }
    >
      <div className="flex flex-col items-center text-center">
        <div
          className={
            emphasis
              ? 'mb-3 flex h-11 w-11 items-center justify-center rounded-sheet bg-primary text-white'
              : 'mb-3 flex h-11 w-11 items-center justify-center rounded-sheet bg-primary/10 text-primary'
          }
        >
          {icon}
        </div>
        <div className="text-sm font-bold text-gray-900">{title}</div>
        <div className="mt-0.5 text-xs text-gray-500">{subtitle}</div>
      </div>
      <div className="mt-3 flex-1 space-y-1.5 text-xs text-gray-600">{children}</div>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-gray-400">{label}</span>
      <span className="truncate text-right font-medium text-gray-700" title={value}>
        {value || '-'}
      </span>
    </div>
  )
}

/** A plain configured/not-configured pill, for the summary mode's workflow and ERP cards. */
function ConfiguredBadge({ configured }: { configured: boolean }) {
  return (
    <Badge tone={configured ? 'success' : 'neutral'} size="md" dot>
      {configured ? 'Configured' : 'Not configured'}
    </Badge>
  )
}

function Connector() {
  return (
    <div className="hidden items-center lg:flex">
      <div className="h-0.5 w-full bg-[repeating-linear-gradient(to_right,theme(colors.brand.300)_0,theme(colors.brand.300)_5px,transparent_5px,transparent_10px)]" />
    </div>
  )
}

/**
 * The three connections a run passes through, left to right. Used both as the
 * workflow's own page ("full") and as an expanded row in the list ("summary"),
 * so the two cannot drift apart.
 *
 * Summary mode drops the setup links (the listing isn't the place to start
 * editing) and cuts each card down to the one thing that says whether it's
 * ready: connection state for the mailbox, configured/not for the flow and
 * the ERP.
 */
export function WorkflowConnectionCards({
  organizationId,
  workflowId,
  workflowType,
  mode = 'full',
}: {
  organizationId: string
  workflowId: string
  workflowType?: string
  mode?: 'full' | 'summary'
}) {
  const summary = mode === 'summary'
  const { isRoot } = useIsRootOrganization()
  const setupPath = `/admin/organizations/${organizationId}/workflows/${workflowId}/setup`
  const typeLabel = WORKFLOW_TYPES.find((t) => t.value === workflowType)?.label ?? workflowType

  const { data: flow } = useWorkflowFlow(organizationId, workflowId)
  const { data: email } = useWorkflowEmailSettings(organizationId, workflowId)
  const { data: erp } = useWorkflowErpSettings(organizationId, workflowId)
  const fetchEmails = useTriggerFetchEmails(organizationId, workflowId)

  const providerLabel =
    ERP_PROVIDERS.find((p) => p.value === erp?.provider)?.label ?? erp?.provider ?? ''

  async function onFetchEmailsNow() {
    try {
      await fetchEmails.mutateAsync()
      toast({ title: 'Fetch triggered', description: 'The mailbox will be read shortly.' })
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not trigger the fetch',
      })
    }
  }

  return (
    <div className="grid items-stretch gap-4 lg:grid-cols-[1fr_48px_1fr_48px_1fr]">
      <Node
        icon={<Mail className="h-5 w-5" />}
        title="Microsoft Outlook"
        subtitle="Graph API mailbox"
        action={
          summary ? undefined : (
            <div className="space-y-2">
              <Button asChild variant="outline" className="w-full">
                <Link to={`${setupPath}/email`}>Manage connection</Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={onFetchEmailsNow}
                disabled={fetchEmails.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {fetchEmails.isPending ? 'Triggering…' : 'Fetch emails now'}
              </Button>
            </div>
          )
        }
      >
        <div className="mb-2 flex justify-center">
          <ConnectionState state={email?.connection_state ?? 'unconfigured'} />
        </div>
        <Field label="Address" value={email?.address ?? ''} />
        {!summary && (
          <Field
            label="Inbox"
            value={email?.use_default_inbox ? 'Default inbox' : (email?.inbox ?? '')}
          />
        )}
        <Field label="Last connected" value={formatTimestamp(email?.last_connected_at ?? null)} />
      </Node>

      <Connector />

      <Node
        emphasis
        icon={<Share2 className="h-5 w-5" />}
        title="FrontFlow"
        subtitle={typeLabel ?? 'Workflow'}
        action={
          summary ? undefined : (
            <Button asChild className="w-full">
              <Link to={`${setupPath}/flow`}>
                <Settings className="h-4 w-4" />
                {isRoot ? 'Edit flow' : 'View flow'}
              </Link>
            </Button>
          )
        }
      >
        {summary ? (
          <div className="flex justify-center">
            <ConfiguredBadge configured={!!flow} />
          </div>
        ) : flow ? (
          <>
            <Field label="Starts at" value={flow.start} />
            <Field label="Ends at" value={flow.end.block} />
            <Field label="Transitions" value={String(flow.transitions?.length ?? 0)} />
            <Field label="Fallbacks" value={String(flow.fallbacks?.length ?? 0)} />
          </>
        ) : (
          <p className="pt-2 text-center text-gray-400">No flow set. The workflow cannot run.</p>
        )}
      </Node>

      <Connector />

      <Node
        icon={<Settings className="h-5 w-5" />}
        title={providerLabel || 'No ERP'}
        subtitle="ERP connection"
        action={
          summary ? undefined : (
            <Button asChild variant="outline" className="w-full">
              <Link to={`${setupPath}/erp`}>Manage connection</Link>
            </Button>
          )
        }
      >
        {summary ? (
          // Once ERP connections are monitored, this can show ConnectionState
          // the way the mailbox card does instead of just configured/not.
          <div className="flex justify-center">
            <ConfiguredBadge configured={!!providerLabel} />
          </div>
        ) : (
          <>
            <Field label="Provider" value={providerLabel} />
            <p className="pt-2 text-gray-400">Connection state is not monitored for the ERP yet.</p>
          </>
        )}
      </Node>
    </div>
  )
}
